import { defineStore } from 'pinia';
import { AxiosResponse } from 'axios';
import axios from '../../../axios';
import {
    ObjectResponse,
    User,
    UpdateUserPasswordValidationError,
    confirmTwoFactorAuthenticationCodeValidationError,
    confirmPasswordCodeValidationError,
} from '../../../types';
import { useCore } from '../composables/core';
import { NotificationType } from 'resources/js/types';

const { coreState } = useCore();
const endpointUrl = '/api/v1/users';

export const useUserStore = defineStore('user', {
    state: () => ({
        currentUser: null as User | null,
        twoFactorAuthenticationQrCode: null as string | null,

        isFetchingUser: false,
        pendingFetchPromise: null as Promise<void> | null,
        isUpdatingUserPassword: false,
        updatePasswordValidationError: null as UpdateUserPasswordValidationError | null,
        isSendingFeedback: false,
        sendFeedbackValidationErrors: false,

        isActivatingTwoFactorAuthentication: false,
        isDeactivatingTwoFactorAuthentication: false,
        isFetchingTwoFactorAuthenticationQrCode: false,
        isConfirmingTwoFactorAuthenticationCode: false,
        confirmTwoFactorAuthenticationCodeValidationError:
            null as confirmTwoFactorAuthenticationCodeValidationError | null,
        isFetchingTwoFactorAuthenticationRecoveryCodes: false,
        isConfirmingPassword: false,
        confirmPasswordValidationError: null as confirmPasswordCodeValidationError | null,
        isFetchingConfirmedPasswordStatus: false,
    }),
    actions: {
        async ensureFetchOnce() {
            if (this.currentUser) {
                return;
            }

            if (this.pendingFetchPromise) {
                return this.pendingFetchPromise;
            }

            this.pendingFetchPromise = (async () => {
                try {
                    await this.fetchCurrentUser();
                } finally {
                    this.pendingFetchPromise = null;
                }
            })();

            return this.pendingFetchPromise;
        },
        async fetchCurrentUser() {
            try {
                this.isFetchingUser = true;
                const response: AxiosResponse<ObjectResponse> = await axios.get(
                    `${endpointUrl}/current`
                );
                this.currentUser = response.data.data as User;
                return Promise.resolve();
            } catch (error) {
                return Promise.reject(error);
            } finally {
                this.isFetchingUser = false;
            }
        },
        async waitForCurrentUser(timeout = 10000) {
            if (this.currentUser) {
                return this.currentUser;
            }

            if (this.isFetchingUser) {
                return new Promise((resolve, reject) => {
                    const checkInterval = 100;
                    let elapsedTime = 0;

                    const intervalId = setInterval(() => {
                        elapsedTime += checkInterval;

                        if (this.currentUser) {
                            clearInterval(intervalId);
                            resolve(this.currentUser);
                        }

                        if (!this.isFetchingUser && !this.currentUser) {
                            clearInterval(intervalId);
                            reject(new Error('Failed to load user'));
                        }

                        if (elapsedTime >= timeout) {
                            clearInterval(intervalId);
                            reject(new Error('Timeout waiting for user to load'));
                        }
                    }, checkInterval);
                });
            }
        },
        async updateCurrentUserPassword(fields) {
            try {
                this.isUpdatingUserPassword = true;
                this.updatePasswordValidationError = null;
                const response: AxiosResponse<ObjectResponse> = await axios.put(
                    `${endpointUrl}/current/update-password`,
                    fields
                );
                if (response.data.status === 422) {
                    this.updatePasswordValidationError = response.data.errors;
                } else {
                    this.currentUser = response.data.data as User;
                }
                return Promise.resolve();
            } catch (error) {
                return Promise.reject(error);
            } finally {
                this.isUpdatingUserPassword = false;
            }
        },
        async sendFeedback(message: string) {
            try {
                this.isSendingFeedback = true;
                const response: AxiosResponse<ObjectResponse> = await axios.post(
                    `${endpointUrl}/current/send-feedback`,
                    { message }
                );
                if (response.data.status === 422) {
                    this.sendFeedbackValidationErrors = response.data.errors;
                }
                return Promise.resolve();
            } catch (error) {
                return Promise.reject(error);
            } finally {
                this.isSendingFeedback = false;
            }
        },
        async activateTwoFactorAuthentication() {
            try {
                this.isActivatingTwoFactorAuthentication = true;
                await axios.post('/user/two-factor-authentication');
                return Promise.resolve();
            } catch (error) {
                return Promise.reject(error);
            } finally {
                this.isActivatingTwoFactorAuthentication = false;
            }
        },
        async deactivateTwoFactorAuthentication() {
            try {
                this.isDeactivatingTwoFactorAuthentication = true;
                await axios.delete('/user/two-factor-authentication');
                return Promise.resolve();
            } catch (error) {
                return Promise.reject(error);
            } finally {
                this.isDeactivatingTwoFactorAuthentication = false;
            }
        },
        async fetchTwoFactorAuthenticationQrCode() {
            try {
                this.isDeactivatingTwoFactorAuthentication = true;
                const response = await axios.get('/user/two-factor-qr-code ');
                this.twoFactorAuthenticationQrCode = response.data.svg;
                return Promise.resolve();
            } catch (error) {
                return Promise.reject(error);
            } finally {
                this.isDeactivatingTwoFactorAuthentication = false;
            }
        },
        async confirmTwoFactorAuthenticationCode(fields) {
            try {
                this.isConfirmingTwoFactorAuthenticationCode = true;
                const response = await axios.post(
                    '/user/confirmed-two-factor-authentication',
                    fields
                );
                this.twoFactorAuthenticationQrCode = response.data.svg;
                return Promise.resolve();
            } catch (error) {
                if (error.response.status === 422) {
                    this.confirmTwoFactorAuthenticationCodeValidationError =
                        error.response.data.errors;
                }
                return Promise.reject(error);
            } finally {
                this.isConfirmingTwoFactorAuthenticationCode = false;
            }
        },
        async fetchTwoFactorAuthenticationRecoveryCodes() {
            try {
                this.isDeactivatingTwoFactorAuthentication = true;
                const response = await axios.get('/user/two-factor-recovery-codes');
                return Promise.resolve(response.data);
            } catch (error) {
                return Promise.reject(error);
            } finally {
                this.isDeactivatingTwoFactorAuthentication = false;
            }
        },
        async confirmPassword(password: string) {
            try {
                this.isConfirmingPassword = true;
                await axios.post('/user/confirm-password', { password });
                this.confirmPasswordValidationError = null;
                return Promise.resolve();
            } catch (error) {
                if (error.response.status === 422) {
                    this.confirmPasswordValidationError = error.response.data.errors;
                }
                return Promise.reject(error);
            } finally {
                this.isConfirmingPassword = false;
            }
        },
        async fetchConfirmedPasswordStatus() {
            try {
                this.isFetchingConfirmedPasswordStatus = true;
                const result = await axios.get('/user/confirmed-password-status');
                return Promise.resolve(result.data.confirmed);
            } catch (error) {
                return Promise.reject(error);
            } finally {
                this.isFetchingConfirmedPasswordStatus = false;
            }
        },
        async toggleNotificationType(
            notificationType: NotificationType,
            isChecked: boolean,
            channelEmail?: boolean,
            channelInbox?: boolean
        ) {
            try {
                const response = await axios.put<ObjectResponse>(
                    `${endpointUrl}/notification-types/${notificationType}`,
                    {
                        is_checked: isChecked,
                        channel_email: channelEmail,
                        channel_inbox: channelInbox,
                    }
                );
                this.currentUser = response.data.data as User;
                return Promise.resolve();
            } catch (error) {
                return Promise.reject(error);
            }
        },
    },
    getters: {
        canManageContacts: (state) => {
            return state.currentUser?.permissions?.can_manage_contacts ?? false;
        },

        canManageCustomer: (state) => {
            return state.currentUser?.permissions?.can_manage_customer ?? false;
        },

        canAccessReceipts: (state) => {
            return state.currentUser?.permissions?.can_access_receipts ?? false;
        },

        canAccessServer: (state) => {
            const hasFeature = state.currentUser?.available_features?.has_server ?? false;
            const hasPermission = state.currentUser?.permissions?.can_access_services ?? false;
            return hasPermission && hasFeature;
        },
        canAccessIpam: (state) => {
            const hasFeature = state.currentUser?.available_features?.has_ipam ?? false;
            const hasPermission = state.currentUser?.permissions?.can_access_services ?? false;
            return hasPermission && hasFeature;
        },
        canAccessColocation: (state) => {
            const hasFeature = state.currentUser?.available_features?.has_colocation ?? false;
            const hasPermission = state.currentUser?.permissions?.can_access_services ?? false;
            return hasPermission && hasFeature;
        },
        canAccessRent: (state) => {
            const hasFeature = state.currentUser?.available_features?.has_rent ?? false;
            const hasPermission = state.currentUser?.permissions?.can_access_services ?? false;
            return hasPermission && hasFeature;
        },
        canBookDatacenterVisit: (state) => {
            if (!coreState.isFeatureDatacenterVisitAvailable) {
                return false;
            }
            const canBookForSelf =
                state.currentUser?.permissions?.can_book_datacenter_visit_for_self ?? false;
            const canBookForOthers =
                state.currentUser?.permissions?.can_book_datacenter_visit_for_others ?? false;

            return canBookForSelf || canBookForOthers;
        },
        canBookDatacenterVisitForSelf: (state) => {
            if (!coreState.isFeatureDatacenterVisitAvailable) {
                return false;
            }

            return state.currentUser?.permissions?.can_book_datacenter_visit_for_self ?? false;
        },

        canBookDatacenterVisitForOthers: (state) => {
            if (!coreState.isFeatureDatacenterVisitAvailable) {
                return false;
            }

            return state.currentUser?.permissions?.can_book_datacenter_visit_for_others ?? false;
        },
        canAccessPayment: (state) => {
            const hasFeature = state.currentUser?.available_features?.has_payment ?? false;
            const hasPermission = state.currentUser?.permissions?.can_access_receipts ?? false;
            return hasPermission && hasFeature;
        },
    },
});
